import * as Yup from 'yup'
import { getInitialState } from '../../../utils/formikUpdate'
import { fetchAllCantones } from '../../../services/cantonService'

/**
 * Valida que un string sea un GeoJSON válido
 */
const isValidGeoJSON = value => {
  if (!value) return false
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    const validTypes = [
      'FeatureCollection',
      'Feature',
      'Polygon',
      'MultiPolygon',
      'Point',
      'LineString',
      'MultiPoint',
      'MultiLineString',
      'GeometryCollection',
    ]
    return parsed && validTypes.includes(parsed.type)
  } catch {
    return false
  }
}

export const datasetFormik = async () => {
  // Obtener cantones para el selector de referencia del mapa
  let cantonesOptions = {}
  let cantonesData = []
  try {
    const cantones = await fetchAllCantones()
    cantonesData = cantones
    cantones.forEach(c => {
      cantonesOptions[c.id] = c.name || c.nombre
    })
  } catch (err) {
    console.error('Error loading cantones:', err)
  }

  // Cantones filtrados: los que tienen slug (necesario para buscar el boundary)
  // Preferir los que tienen hasGeojson=true, pero si el backend no lo devuelve, mostrar todos con slug
  const tieneHasGeojson = cantonesData.some(c => c.hasGeojson)
  let cantonesConBoundary = {}
  cantonesData
    .filter(c => c.slug && (!tieneHasGeojson || c.hasGeojson))
    .forEach(c => {
      cantonesConBoundary[c.id] = c.name
    })

  // === PASO 1: INFORMACIÓN BÁSICA ===
  const STEP1_FIELDS = [
    {
      x: 12,
      name: 'nombre',
      initvalue: '',
      label: 'Nombre del Tenant *',
      helperText: 'Nombre corto del tenant (máximo 100 caracteres)',
    },
    {
      x: 12,
      name: 'nombre_completo',
      initvalue: '',
      label: 'Nombre Completo *',
      helperText: 'Ej: Ciudad de Loja (máximo 150 caracteres)',
    },
    {
      x: 6,
      name: 'slug',
      initvalue: '',
      label: 'Slug (URL) *',
      helperText: 'Solo minúsculas, números y guiones (máx 50)',
    },
    {
      x: 6,
      name: 'codigo',
      initvalue: '',
      label: 'Código *',
      helperText: 'Código identificador (máximo 50 caracteres)',
    },
    {
      x: 6,
      name: 'orden',
      initvalue: 0,
      label: 'Orden',
      helperText: 'Orden de visualización',
      type: 'number',
    },
    {
      x: 6,
      name: 'habilitado',
      initvalue: 1,
      label: 'Estado',
      options: { 0: 'Desactivado', 1: 'Activado' },
    },
  ]

  // === PASO 2: BOUNDARY GEOJSON ===
  // El selector de cantón se muestra aquí para que el usuario vea de inmediato
  // los límites del cantón en el mapa al dibujar el boundary del tenant.
  const STEP2_FIELDS = [
    {
      x: 12,
      name: 'id_canton_ref',
      initvalue: '',
      label: 'Cantón de referencia',
      helperText: 'Selecciona un cantón como guía para ver sus límites',
      options: cantonesConBoundary,
      isCantonRef: true,
    },
    {
      x: 12,
      name: 'boundary_geojson',
      initvalue: '',
      label: 'Boundary GeoJSON',
      isGeoJSONInput: true,
    },
  ]

  // === PASO 3: CONFIGURACIÓN AVANZADA ===
  const STEP3_FIELDS = [
    {
      x: 6,
      name: 'center_lng',
      initvalue: '',
      label: 'Longitud (Centro)',
      helperText: 'Opcional - se calcula automáticamente',
      type: 'number',
    },
    {
      x: 6,
      name: 'center_lat',
      initvalue: '',
      label: 'Latitud (Centro)',
      helperText: 'Opcional - se calcula automáticamente',
      type: 'number',
    },
    {
      x: 4,
      name: 'zoom',
      initvalue: '',
      label: 'Zoom',
      helperText: 'Valor entre 1-22',
      type: 'number',
    },
    {
      x: 4,
      name: 'bounds_sw_lng',
      initvalue: '',
      label: 'SW Longitud',
      helperText: 'Límite suroeste',
      type: 'number',
    },
    {
      x: 4,
      name: 'bounds_sw_lat',
      initvalue: '',
      label: 'SW Latitud',
      helperText: 'Límite suroeste',
      type: 'number',
    },
    {
      x: 6,
      name: 'bounds_ne_lng',
      initvalue: '',
      label: 'NE Longitud',
      helperText: 'Límite noreste',
      type: 'number',
    },
    {
      x: 6,
      name: 'bounds_ne_lat',
      initvalue: '',
      label: 'NE Latitud',
      helperText: 'Límite noreste',
      type: 'number',
    },
  ]

  // Todos los campos combinados
  const ITEMS_TENANT_FORMIK = [...STEP1_FIELDS, ...STEP2_FIELDS, ...STEP3_FIELDS]

  const INITIAL_FORMIK_STATE = getInitialState(ITEMS_TENANT_FORMIK)

  // Validaciones por paso
  const STEP1_VALIDATION = Yup.object().shape({
    nombre: Yup.string()
      .min(3, 'Mínimo 3 caracteres')
      .max(100, 'Máximo 100 caracteres')
      .required('Obligatorio'),
    nombre_completo: Yup.string()
      .min(3, 'Mínimo 3 caracteres')
      .max(150, 'Máximo 150 caracteres')
      .required('Obligatorio'),
    slug: Yup.string()
      .min(2, 'Mínimo 2 caracteres')
      .max(50, 'Máximo 50 caracteres')
      .matches(/^[a-z0-9-]+$/, 'Solo letras minúsculas, números y guiones')
      .required('Obligatorio'),
    codigo: Yup.string()
      .min(2, 'Mínimo 2 caracteres')
      .max(50, 'Máximo 50 caracteres')
      .required('Obligatorio'),
  })

  const STEP2_VALIDATION = Yup.object().shape({
    boundary_geojson: Yup.string()
      .required('Debe proporcionar un GeoJSON')
      .test('is-valid-geojson', 'Debe ser un GeoJSON válido', isValidGeoJSON),
  })

  const STEP3_VALIDATION = Yup.object().shape({
    center_lng: Yup.number()
      .min(-180, 'Longitud mínima -180')
      .max(180, 'Longitud máxima 180')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
    center_lat: Yup.number()
      .min(-90, 'Latitud mínima -90')
      .max(90, 'Latitud máxima 90')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
    zoom: Yup.number()
      .min(1, 'Zoom mínimo 1')
      .max(22, 'Zoom máximo 22')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
    bounds_sw_lng: Yup.number()
      .min(-180, 'Longitud mínima -180')
      .max(180, 'Longitud máxima 180')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
    bounds_sw_lat: Yup.number()
      .min(-90, 'Latitud mínima -90')
      .max(90, 'Latitud máxima 90')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
    bounds_ne_lng: Yup.number()
      .min(-180, 'Longitud mínima -180')
      .max(180, 'Longitud máxima 180')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
    bounds_ne_lat: Yup.number()
      .min(-90, 'Latitud mínima -90')
      .max(90, 'Latitud máxima 90')
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Debe ser un número'),
  })

  // Validación completa
  const FORM_VALIDATION =
    STEP1_VALIDATION.concat(STEP2_VALIDATION).concat(STEP3_VALIDATION)

  return {
    INITIAL_FORMIK_STATE,
    ITEMS_TENANT_FORMIK,
    STEP1_FIELDS,
    STEP2_FIELDS,
    STEP3_FIELDS,
    STEP1_VALIDATION,
    STEP2_VALIDATION,
    STEP3_VALIDATION,
    FORM_VALIDATION,
    cantonesData,
  }
}
